import React, { useState, useCallback, useMemo, useEffect } from 'react'
import DataSource from 'devextreme/data/data_source'
import { SelectBox } from 'devextreme-react/select-box'
import { Form, Formik } from 'formik'
import Div from '@jumbo/shared/Div'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import LoadingButton from '@mui/lab/LoadingButton'
import SearchIcon from '@mui/icons-material/Search'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import CustomStore from 'devextreme/data/custom_store'
import utilServices from 'app/services/util-services'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import familyAndMemberService from 'app/services/pages/reports/registrations/familyAndMemberService/familyAndMemberService'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import { useDispatch, useSelector } from 'react-redux'
import { getMedicalConditionTypeLookupList } from 'app/redux/store/features/medcond'

const severityFilterItems = [
    { value: 0, text: 'Normal' },
    { value: 1, text: 'Medium' },
    { value: 2, text: 'High' },
    { value: 3, text: 'Critical' },
]

const MedicalInformationReportFilter = ({ mutation }) => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()
    const { lookups: medicalConditionTypeLookups } = useSelector((state) => state.medcond)

    let initialValues = {
        sort: 'FirstName',
    }

    const [filter, setFilter] = useState({ customerId: null })
    const initialFilterFields = {
        familySelectedData: null,
        memberSelectedData: null,
        medicalTypeId: null,
        severity: null,
        includeMembersWithoutMedicalConditions: false,
    }

    const [filterFields, setFilterFields] = useState({ ...initialFilterFields })

    useEffect(() => {
        dispatch(getMedicalConditionTypeLookupList({}))
    }, [dispatch])

    const handleError = useCallback(
        (error) => {
            if (error.status === '401') {
                navigate('/profile/signout')
            } else if (error.detail) {
                enqueueSnackbar(error.detail, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            } else {
                enqueueSnackbar(error, {
                    variant: 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                })
            }
        },
        [enqueueSnackbar, navigate]
    )

    const onFamilyValueChanged = async (e) => {
        if (e) {
            setFilterFields((prev) => ({
                ...prev,
                familySelectedData: e.id,
            }))
        } else {
            setFilterFields((prev) => ({
                ...prev,
                familySelectedData: null,
                memberSelectedData: null,
            }))
        }
    }

    const onMemberValueChanged = (e) => {
        if (e) {
            setFilterFields((prev) => ({
                ...prev,
                memberSelectedData: e.id,
            }))
        } else {
            setFilterFields((prev) => ({
                ...prev,
                memberSelectedData: null,
            }))
        }
    }

    const familyLookupList = useCallback(async (searchTerm) => {
        try {
            return await familyAndMemberService.getFamilyLookupList({
                familyName: searchTerm,
            })
        } catch (error) {
            handleError(error)
            return []
        }
    }, [])

    const familyLookupById = useCallback(async (key) => {
        try {
            return await familyAndMemberService.getFamilyLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await familyLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return familyLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const memberLookupList = useCallback(
        async (searchTerm) => {
            try {
                if (utilServices.isNullOrUndefined(filterFields.familySelectedData))
                    return { totalCount: 0, data: [] }
                return await familyAndMemberService.getMemberLookupList({
                    familyId: filterFields.familySelectedData,
                    firstName: searchTerm,
                })
            } catch (error) {
                handleError(error)
            }
        },
        [filterFields.familySelectedData]
    )

    const memberLookupById = useCallback(
        async (key) => {
            try {
                return await familyAndMemberService.getMemberLookupById(filterFields.familySelectedData, key)
            } catch (error) {
                handleError(error)
            }
        },
        [familyAndMemberService, filterFields.familySelectedData]
    )

    const memberStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await memberLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return memberLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        [filterFields.familySelectedData]
    )

    const customerLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({
                firstName: searchTerm,
            })
        } catch (error) {
            handleError(error)
            return []
        }
    }, [])

    const customerLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const customerStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await customerLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return customerLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const onFormSubmit = () => {
        mutation.mutate({
            customerId: utilServices.isNullOrUndefined(filter.customerId) ? null : filter.customerId,
            familyId: utilServices.isNullOrUndefined(filterFields.familySelectedData)
                ? null
                : filterFields.familySelectedData,
            memberId: utilServices.isNullOrUndefined(filterFields.memberSelectedData)
                ? null
                : filterFields.memberSelectedData,
            medicalConditionTypeId: utilServices.isNullOrUndefined(filterFields.medicalTypeId)
                ? null
                : filterFields.medicalTypeId,
            severity:
                filterFields.severity === null || filterFields.severity === undefined ? null : filterFields.severity,
            includeMembersWithoutMedicalConditions: !!filterFields.includeMembersWithoutMedicalConditions,
            sort: 'FirstName',
            isDescending: false,
        })
    }

    const onFilterSubmit = (data, { setSubmitting }) => {
        setSubmitting(true)
        onFormSubmit(data.sort)
        setSubmitting(false)
    }

    const handleClear = () => {
        setFilterFields({ ...initialFilterFields })
        setFilter({ customerId: null })
    }

    const onCustomerSelectionChanged = (value) => {
        setFilter({ ...filter, customerId: value?.id })
    }

    return (
        <Formik
            validateOnChange={true}
            initialValues={initialValues}
            onReset={onFilterSubmit}
            onSubmit={onFilterSubmit}>
            {({ isSubmitting }) => (
                <Form noValidate autoComplete='off'>
                    <JumboCardQuick noWrapper id='filterBody' variant={'primary'} sx={{ display: '', borderRadius: 0 }}>
                        <Divider />
                        <Div
                            className='report-filter-parent'
                            style={{ display: 'flex', margin: '0% 1.5% 1.5% 1.5%', flexWrap: 'wrap' }}>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Customer </Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 220 }}
                                        dataSource={customerStore}
                                        itemTemplate={(data) => data.firstName + '  ' + data.lastName}
                                        displayExpr={(data) => (data ? data.firstName + '  ' + data.lastName : '')}
                                        value={filter.customerId}
                                        searchEnabled={true}
                                        onValueChange={onCustomerSelectionChanged}
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Family </Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 220 }}
                                        dataSource={familyStore}
                                        value={filterFields.familySelectedData}
                                        displayExpr={'familyName'}
                                        searchEnabled={true}
                                        onValueChange={onFamilyValueChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Participant </Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 220 }}
                                        dataSource={memberStore}
                                        itemTemplate={(data) => data.firstName + '  ' + data.lastName}
                                        displayExpr={(data) => (data ? data.firstName + '  ' + data.lastName : '')}
                                        value={filterFields.memberSelectedData}
                                        searchEnabled={true}
                                        onValueChange={onMemberValueChanged}
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Condition type</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 260 }}
                                        dataSource={medicalConditionTypeLookups}
                                        displayExpr={'name'}
                                        valueExpr={'id'}
                                        value={filterFields.medicalTypeId}
                                        searchEnabled={true}
                                        showClearButton={true}
                                        onValueChange={(v) =>
                                            setFilterFields((prev) => ({ ...prev, medicalTypeId: v ?? null }))
                                        }
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Severity</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 160 }}
                                        items={severityFilterItems}
                                        displayExpr={'text'}
                                        valueExpr={'value'}
                                        value={filterFields.severity}
                                        showClearButton={true}
                                        onValueChange={(v) =>
                                            setFilterFields((prev) => ({ ...prev, severity: v }))
                                        }
                                    />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5, minWidth: 280, alignSelf: 'flex-end' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!filterFields.includeMembersWithoutMedicalConditions}
                                            onChange={(e) =>
                                                setFilterFields((prev) => ({
                                                    ...prev,
                                                    includeMembersWithoutMedicalConditions: e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label={
                                        <Typography variant='body2'>
                                            Include members without any medical condition recorded
                                        </Typography>
                                    }
                                />
                            </Div>
                            <Div className='report-filter-Button' sx={{ mt: 4.5 }}>
                                <Stack direction={'row'} spacing={1}>
                                    <LoadingButton
                                        size={'small'}
                                        type='submit'
                                        variant={'contained'}
                                        loading={isSubmitting || mutation.isLoading}>
                                        <SearchIcon sx={{ fontSize: 18 }} /> Search
                                    </LoadingButton>
                                    <LoadingButton
                                        size={'small'}
                                        type={'reset'}
                                        onClick={handleClear}
                                        variant={'contained'}>
                                        <AutorenewRoundedIcon sx={{ fontSize: 18 }} /> Clear
                                    </LoadingButton>
                                </Stack>
                            </Div>
                        </Div>
                    </JumboCardQuick>
                </Form>
            )}
        </Formik>
    )
}

export default MedicalInformationReportFilter
